import {map, isEmpty, some} from 'lodash';
import {Fragment, useMemo, useState, useEffect, useCallback} from 'react';
import {useTranslation} from 'react-i18next';

import {userSettingsApi} from 'api';
import {ActionButton, DataWrapper, PredefinedNotifierType} from 'components';
import {ConfirmModal} from 'components/modals';
import {useApiData} from 'hooks';
import {Group} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale/translation-keys';

import {RecipientCard} from './RecipientCard';
import {RecipientModal} from './RecipientModal';

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.SESSION.REQUEST_APPOINTMENT_SECTION;

type Props = {
    groups: Group[]
};

export const RequestAppointment: React.FC<Props> = ({groups}: Props) => {
    const [confirmRemoveModalOpen, setConfirmRemoveModalOpen] = useState<boolean>(false);
    const [groupToRemove, setGroupToRemove] = useState<string>('');
    const [recipientMap, setRecipientMap] = useState<{[key: string]: string[]}>({});
    const isEmptyRecipientMap = useMemo(() =>
        isEmpty(recipientMap) || !some(recipientMap, (value) => !isEmpty(value)), [recipientMap]);
    const [recipientModalProps, setRecipientModalProps] = useState({
        open: false,
        isEditing: true,
        group: null,
        groups: [],
        recipients: [],
        showAllGroupsOption: true
    });
    const {t} = useTranslation();

    const onSuccess = useCallback((data: {[key: string]: string[]}) => {
        setRecipientMap(data);
    }, []);

    const onError = useCallback(() => {
        setRecipientMap({});
    }, []);

    const {send: fetchRecipients, loading: fetchLoading} = useApiData<{[key: string]: string[]}>(
        userSettingsApi.getRequestAppointmentRecipients, {
            onSuccess,
            onError
        });

    const onSuccessUpdateRecipients = useCallback(() => {
        fetchRecipients();
        setGroupToRemove('');
        setConfirmRemoveModalOpen(false);
    }, [fetchRecipients]);

    const {send: updateRecipients, loading: updateLoading} = useApiData(userSettingsApi.updateRequestAppointmentRecipients, {
        onSuccess: onSuccessUpdateRecipients,
        notificationType: PredefinedNotifierType.Delete
    });

    const remainingGroups = useMemo(() =>
        groups.filter((group) => isEmpty(recipientMap[group.id])), [groups, recipientMap]);

    const addBtnVisible = useMemo(() => remainingGroups.length || isEmpty(recipientMap.account), [remainingGroups, recipientMap]);

    const openEditModal = useCallback((group: Group) => {
        const recipientMapKey = group?.id || 'account';
        setRecipientModalProps({
            open: true,
            isEditing: true,
            group,
            groups,
            recipients: recipientMap[recipientMapKey],
            showAllGroupsOption: false
        });
    }, [recipientMap, groups]);

    const openResetModal = useCallback((group: Group) => {
        const groupId = group?.id || '';
        setGroupToRemove(groupId);
        setConfirmRemoveModalOpen(true);
    }, []);

    const openAddEmailModal = useCallback(() => {
        setRecipientModalProps({
            open: true,
            isEditing: false,
            group: null,
            groups: remainingGroups,
            recipients: [],
            showAllGroupsOption: isEmpty(recipientMap.account)
        });
    }, [remainingGroups, recipientMap]);

    const handleCloseEditRecipientModal = useCallback((recipientsUpdated: boolean) => {
        setRecipientModalProps({
            open: false,
            isEditing: true,
            group: null,
            groups,
            recipients: [],
            showAllGroupsOption: true
        });

        if (recipientsUpdated) {
            fetchRecipients();
        }
    }, [groups, fetchRecipients]);

    const handleCloseConfirmRemoveModal = useCallback((confirm: boolean) => {
        if (confirm) {
            updateRecipients([], groupToRemove);
        } else {
            setGroupToRemove('');
            setConfirmRemoveModalOpen(false);
        }
    }, [groupToRemove, updateRecipients]);

    useEffect(() => {
        fetchRecipients();
    }, [fetchRecipients]);

    return (
        <Fragment>
            <div className='tw-mb-4'>{t(T_KEYS.DESCRIPTION)}</div>
            {addBtnVisible &&
                <ActionButton action='create' className='tw-mr-2' onClick={openAddEmailModal}>
                    {t(T_KEYS.ADD_BTN)}
                </ActionButton>}

            <DataWrapper noData={isEmptyRecipientMap} isLoading={fetchLoading || updateLoading}>
                <RecipientCard
                    title={t(T_KEYS.ALL_GROUPS)}
                    emails={recipientMap.account}
                    onEdit={openEditModal}
                    onReset={openResetModal}
                />
                {map(groups, (group) => (
                    <RecipientCard
                        key={group.id}
                        title={group.name}
                        group={group}
                        emails={recipientMap[group.id] || []}
                        onEdit={openEditModal}
                        onReset={openResetModal}
                    />
                ))}
            </DataWrapper>

            <RecipientModal
                {...recipientModalProps}
                onClose={handleCloseEditRecipientModal}
            />

            <ConfirmModal
                open={confirmRemoveModalOpen}
                title={t(T_KEYS.CONFIRM_REMOVE_TITLE)}
                onClose={handleCloseConfirmRemoveModal}
            />
        </Fragment>
    );
};
